import React, { useCallback, useState } from 'react';

import type { IntlShape } from 'react-intl';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { PortalProviderAPI } from '@atlaskit/editor-common/common';
import { isSSR } from '@atlaskit/editor-common/core-utils';
import type { EventDispatcher } from '@atlaskit/editor-common/event-dispatcher';
import { isNativeEmbedExtension } from '@atlaskit/editor-common/extensibility/native-embed';
import { preserveRemixNodeView } from '@atlaskit/editor-common/extensibility/preserve-remix-node-view';
import type {
	ExtensionsPluginInjectionAPI,
	GetPMNodeHeight,
	MacroInteractionDesignFeatureFlags,
} from '@atlaskit/editor-common/extensibility/types';
import { ExtensionNode } from '@atlaskit/editor-common/extensionNodeView';
import { ExtensionNodeWrapper } from '@atlaskit/editor-common/ExtensionNodeWrapper';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions/extension-handler';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { getPosHandler } from '@atlaskit/editor-common/types/editor-plugin';
import type { EditorProps } from '@atlaskit/editor-core/editor';
import type { Node as PmNode } from '@atlaskit/editor-prosemirror/model';
import type { Selection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView, NodeView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isReactCompilerActivePlatform } from '@atlaskit/react-compiler-gating';

import type { DataSourceProvider } from '../data-source-provider';
import { getPluginState } from '../plugins/plugin-key';
import ExtensionWithDataSource from '../ui/Extension';

export type ForwardRef = (node: HTMLElement | null) => void;

type ExtensionNodeViewOptions = {
	allowAIGeneratedContentMotion?: boolean;
	appearance?: NonNullable<EditorProps['appearance']>;
	getExtensionHeight?: GetPMNodeHeight;
};

/**
 * @description This class extends editor-core ExtensionNode passing dataSourceProvider
 * and rendering ExtensionWithDataSource component
 */
export class ExtensionNodeWithDataSource extends ExtensionNode<{
	dataSourceProvider?: DataSourceProvider;
}> {
	render(props: ExtensionRenderProps, forwardRef: ForwardRef): React.JSX.Element {
		return (
			<ExtensionNodeRender
				// EditorView is mutable; expose selection changes to invalidate compiled renders.
				selection={isReactCompilerActivePlatform() ? this.view.state.selection : undefined}
				editorView={this.view as EditorView}
				forwardRef={forwardRef}
				// The getPos arg is always a function when used with nodes
				// the version of the types we use has a union with the type
				// for marks.
				// This has been fixed in later versions of the definitly typed
				// types (and also in prosmirror-views inbuilt types).
				// https://github.com/DefinitelyTyped/DefinitelyTyped/pull/57384
				getPos={this.getPos as () => number}
				node={this.node}
				renderProps={props}
			/>
		);
	}
}

type ExtensionRenderProps = {
	dataSourceProvider?: DataSourceProvider;
	extensionHandlers: ExtensionHandlers;
	extensionLoadingHandlers?: ExtensionHandlers;
	extensionNodeViewOptions: ExtensionNodeViewOptions;
	intl?: IntlShape;
	macroInteractionDesignFeatureFlags?: MacroInteractionDesignFeatureFlags;
	pluginInjectionApi: ExtensionsPluginInjectionAPI;
	providerFactory: ProviderFactory;
	rendererExtensionHandlers?: ExtensionHandlers;
	showLivePagesBodiedMacrosRendererView?: (node: ADFEntity) => boolean;
	showUpdatedLivePages1PBodiedExtensionUI?: (node: ADFEntity) => boolean;
};

type ExtensionNodeRenderProps = {
	editorView: EditorView;
	forwardRef: ForwardRef;
	getPos: () => number;
	node: PmNode;
	renderProps: ExtensionRenderProps;
	selection?: Selection;
};

/**
 * Wraps the extension in its node wrapper. A component rather than part of the node view's `render`,
 * so `contentReady` can be React state: the extension reports its content ready from inside the
 * wrapper, and the wrapper animates the node in once it has.
 */
function ExtensionNodeRender({
	selection,
	editorView,
	forwardRef,
	getPos,
	node,
	renderProps,
}: ExtensionNodeRenderProps): React.JSX.Element {
	const [contentReady, setContentReady] = useState(false);
	const onContentReady = useCallback(() => setContentReady(true), []);

	// Only native embeds animate in; every other extension type renders as it does without the
	// option, so nothing waits on a signal it does not send.
	const generatedContentMotion =
		!!renderProps.extensionNodeViewOptions.allowAIGeneratedContentMotion &&
		isNativeEmbedExtension(node);

	return (
		<ExtensionNodeWrapper
			intl={renderProps.intl}
			nodeType={node.type.name}
			macroInteractionDesignFeatureFlags={renderProps.macroInteractionDesignFeatureFlags}
			generatedContentMotion={generatedContentMotion}
			contentReady={contentReady}
		>
			<ExtensionWithDataSource
				selection={selection}
				editorAppearance={renderProps.extensionNodeViewOptions.appearance}
				editorView={editorView}
				node={node}
				getPos={getPos}
				providerFactory={renderProps.providerFactory}
				handleContentDOMRef={forwardRef}
				extensionHandlers={renderProps.extensionHandlers}
				extensionLoadingHandlers={renderProps.extensionLoadingHandlers}
				dataSourceProvider={renderProps.dataSourceProvider}
				pluginInjectionApi={renderProps.pluginInjectionApi}
				macroInteractionDesignFeatureFlags={renderProps.macroInteractionDesignFeatureFlags}
				showLivePagesBodiedMacrosRendererView={renderProps.showLivePagesBodiedMacrosRendererView}
				showUpdatedLivePages1PBodiedExtensionUI={
					renderProps.showUpdatedLivePages1PBodiedExtensionUI
				}
				rendererExtensionHandlers={renderProps.rendererExtensionHandlers}
				onContentReady={generatedContentMotion ? onContentReady : undefined}
			/>
		</ExtensionNodeWrapper>
	);
}

export default function ExtensionNodeView(
	portalProviderAPI: PortalProviderAPI,
	eventDispatcher: EventDispatcher,
	providerFactory: ProviderFactory,
	extensionHandlers: ExtensionHandlers,
	extensionLoadingHandlers: ExtensionHandlers | undefined,
	extensionNodeViewOptions: ExtensionNodeViewOptions,
	pluginInjectionApi: ExtensionsPluginInjectionAPI,
	macroInteractionDesignFeatureFlags?: MacroInteractionDesignFeatureFlags,
	showLivePagesBodiedMacrosRendererView?: (node: ADFEntity) => boolean,
	showUpdatedLivePages1PBodiedExtensionUI?: (node: ADFEntity) => boolean,
	rendererExtensionHandlers?: ExtensionHandlers,
	intl?: IntlShape,
) {
	return (node: PmNode, view: EditorView, getPos: getPosHandler): NodeView => {
		const pluginState = getPluginState(view.state);

		const renderProps = {
			providerFactory,
			extensionHandlers,
			extensionLoadingHandlers,
			extensionNodeViewOptions,
			dataSourceProvider: pluginState?.dataSourceProvider,
			pluginInjectionApi,
			macroInteractionDesignFeatureFlags,
			showLivePagesBodiedMacrosRendererView,
			showUpdatedLivePages1PBodiedExtensionUI,
			rendererExtensionHandlers,
			intl,
		};
		const createExtensionNode = (currentNode: PmNode) =>
			new ExtensionNodeWithDataSource(
				currentNode,
				view,
				getPos,
				portalProviderAPI,
				eventDispatcher,
				renderProps,
			);

		if (!isSSR() && fg('cc_maui_annotations_on_extensions')) {
			return preserveRemixNodeView({
				node,
				view,
				getPos,
				renderProps,
				rendererKind: ExtensionNodeWithDataSource,
				createExtensionNode,
			});
		}
		return createExtensionNode(node).init();
	};
}
